import React, { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'

const ConsumptionChart = ({
  chartId,
  chartTitle,
  chartData,
  axesNames,
  chartDimensions,
}) => {
  const [colorMapping, setColorMapping] = useState([])

  const chartRef = useRef(null)
  const chartContext = useRef(null)

  const initializeSVG = () => {
    const svg = d3
      .select(chartRef.current)
      .append('svg')
      .attr('viewBox', `0 0 ${chartDimensions.width} ${chartDimensions.height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr(
        'style',
        'max-width: 100%; max-height: 100%; color: #000; margin: 0 auto; font: 13px sans-serif;'
      )

    svg
      .append('clipPath')
      .attr('id', `${chartId}ClipChart`)
      .append('rect')
      .attr('x', chartDimensions.margin.left)
      .attr('y', chartDimensions.margin.top)
      .attr(
        'width',
        chartDimensions.width -
          chartDimensions.margin.left -
          chartDimensions.margin.right
      )
      .attr(
        'height',
        chartDimensions.height -
          chartDimensions.margin.top -
          chartDimensions.margin.bottom
      )

    return svg
  }

  // Create the horizontal and vertical scales.
  const initializeScales = () => {
    let x = null
    x = d3
      .scaleTime()
      .domain(d3.extent(chartData, (d) => new Date(d.x)))
      .range([
        chartDimensions.margin.left,
        chartDimensions.width - chartDimensions.margin.right,
      ])

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(chartData, (d) => d.y)])
      .nice()
      .range([
        chartDimensions.height - chartDimensions.margin.bottom,
        chartDimensions.margin.top,
      ])

    return { x, y }
  }

  const initializeAxes = (svg, scales) => {
    const dateFormat = d3.timeFormat('%d/%m/%Y')

    // Get unique dates from data to determine optimal tick count
    const uniqueDates = [...new Set(chartData.map((d) => d.x.getTime()))].length
    const maxTicks = Math.min(Math.ceil(uniqueDates / 3), 6)

    // Create the horizontal axis generator, called at startup and when zooming.
    const xAxis = (g, x) =>
      g
        .attr('class', 'xAxis')
        .call(
          d3
            .axisBottom(x)
            .ticks(maxTicks)
            .tickFormat(dateFormat)
            .tickSizeOuter(0)
        )

    // Append the horizontal axis.
    const gx = svg
      .append('g')
      .attr(
        'transform',
        `translate(0,${chartDimensions.height - chartDimensions.margin.bottom})`
      )
      .call(xAxis, scales.x)

    gx.selectAll('text').style('font-size', '13px')

    // Append the vertical axis.
    svg
      .append('g')
      .attr('class', 'yAxis')
      .attr('transform', `translate(${chartDimensions.margin.left},0)`)
      .call(d3.axisLeft(scales.y).ticks(null))
      .selectAll('text')
      .style('font-size', '13px')

    return { xAxis, gx, maxTicks }
  }

  const drawGrid = (svg) => {
    svg
      .selectAll('g.yAxis g.tick')
      .append('line')
      .attr('class', 'gridline')
      .attr(
        'x1',
        chartDimensions.width -
          chartDimensions.margin.left -
          chartDimensions.margin.right
      )
      .attr('stroke', '#00000020')
      .attr('stroke-dasharray', 'none')

    svg
      .selectAll('g.xAxis g.tick')
      .append('line')
      .attr('class', 'gridline')
      .attr(
        'y1',
        -chartDimensions.height +
          chartDimensions.margin.bottom +
          chartDimensions.margin.top
      )
      .attr('stroke', '#00000020')
      .attr('stroke-dasharray', 'none')
  }

  const pointermoved = (event, svg, points, path, dot) => {
    const [xm, ym] = d3.pointer(event)
    const i = d3.leastIndex(points, ([x, y]) => Math.hypot(x - xm, y - ym))
    const [x, y, k] = points[i]
    path
      .style('stroke', ({ z }) => (z === k ? null : '#ddd'))
      .filter(({ z }) => z === k)
      .raise()
    dot.attr('transform', `translate(${x},${y})`)
    dot.select('text').text(k)
    svg.property('value', chartData[i]).dispatch('input', { bubbles: true })
  }

  const pointerentered = (path, dot) => {
    path.style('mix-blend-mode', null).style('stroke', '#ddd')
    dot.attr('display', null)
  }

  const pointerleft = (svg, path, dot) => {
    console.log('LEFT')
    path.style('mix-blend-mode', 'multiply').style('stroke', 'steelblue')
    dot.attr('display', 'none')
    svg.node().value = null
    svg.dispatch('input', { bubbles: true })
  }

  const drawLines = (svg, scales, data) => {
    // Compute the points in pixel space as [x, y, z], where z is the name of the series.
    // const points = chartData.map((d) => [
    //   scales.x(d.x),
    //   scales.y(d.y),
    //   d.division,
    // ])

    // // Group the points by series.
    // const groups = d3.rollup(
    //   points,
    //   (v) => Object.assign(v, { z: v[0][2] }),
    //   (d) => d[2]
    // )

    // const line = d3.line()
    // const path = svg
    //   .append('g')
    //   .attr('fill', 'none')
    //   .attr('stroke', 'steelblue')
    //   .attr('stroke-width', 1.5)
    //   .attr('stroke-linejoin', 'round')
    //   .attr('stroke-linecap', 'round')
    //   .selectAll('path')
    //   .data(groups.values())
    //   .join('path')
    //   .style('mix-blend-mode', 'multiply')
    //   .attr('d', line)

    const colorScale = d3.scaleOrdinal(d3.schemeCategory10)

    const line = d3
      .line()
      .x((d) => scales.x(d.x))
      .y((d) => scales.y(d.y))

    const groups = d3.rollup(
      data,
      (v) => v,
      (d) => d.division
    )

    const colorMappingTemp = []

    const path = svg
      .append('g')
      .attr('fill', 'none')
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .selectAll('path')
      .data(groups.values())
      .join('path')
      .attr('clip-path', `url(#${chartId}ClipChart)`)
      .attr('class', 'line')
      .attr('stroke', (d, i) => {
        const color = colorScale(i)
        colorMappingTemp.push({ division: d[0].division, color })
        return color
      })
      .attr('stroke-width', 2)
      .attr('d', line)

    console.log(colorMappingTemp)
    setColorMapping(colorMappingTemp)

    // Add an invisible layer for the interactive tip.
    const dot = svg.append('g').attr('display', 'none')

    dot.append('circle').attr('r', 2.5)

    dot.append('text').attr('text-anchor', 'middle').attr('y', -8)

    // svg
    //   .on('pointerenter', () => pointerentered(path, dot))
    //   .on('pointermove', (e) => pointermoved(e, svg, points, path, dot))
    //   .on('pointerleave', () => pointerleft(svg, path, dot))
    //   .on('touchstart', (event) => event.preventDefault())

    return line
  }

  const handleZoom = (event, grid) => {
    const { scales, gx, xAxis } = chartContext.current

    const xz = event.transform.rescaleX(scales.x)
    d3.select(chartRef.current)
      .select('svg')
      .selectAll('path.line')
      .attr('d', (d) => {
        const updatedLine = d3
          .line()
          .x((point) => xz(point.x))
          .y((point) => scales.y(point.y))

        return updatedLine(d)
      })
    gx.call(xAxis, xz)
    gx.selectAll('text').style('font-size', '13px')

    const xTicks = d3.selectAll('g.xAxis g.tick')

    xTicks.each(function () {
      const tick = d3.select(this)
      const gridline = tick.select('line.gridline')

      if (gridline.empty()) {
        tick
          .append('line')
          .attr('class', 'gridline')
          .attr('x1', 0)
          .attr(
            'y1',
            -chartDimensions.height +
              chartDimensions.margin.bottom +
              chartDimensions.margin.top
          )
          .attr('x2', 0)
          .attr('y2', 0)
          .attr('stroke', '#00000020')
          .style('visibility', grid ? 'visible' : 'hidden')
      }
    })

    d3.select(chartRef.current)
      .select('svg')
      .selectAll('circle.linePoint')
      .attr('cx', (d) => xz(d.x))
      .attr('cy', (d) => scales.y(d.y))
  }

  // Create the zoom behavior.
  const initializeZoomBehavior = () => {
    const zoom = d3
      .zoom()
      .scaleExtent([1, 20])
      .extent([
        [chartDimensions.margin.left, 0],
        [
          chartDimensions.width - chartDimensions.margin.right,
          chartDimensions.height,
        ],
      ])
      .translateExtent([
        [chartDimensions.margin.left, -Infinity],
        [chartDimensions.width - chartDimensions.margin.right, Infinity],
      ])
      .on('zoom', (event) => handleZoom(event, true))

    return zoom
  }

  const renderChart = () => {
    const svg = initializeSVG()
    const scales = initializeScales()
    const { xAxis, gx, maxTicks } = initializeAxes(svg, scales)
    drawGrid(svg)
    const line = drawLines(svg, scales, chartData)
    const zoom = initializeZoomBehavior()
    // initializePointsWithTooltips(svg, scales)

    chartContext.current = {
      scales,
      xAxis,
      gx,
      line,
      zoom,
      maxTicks,
    }

    // Initial zoom.
    svg.call(zoom)
  }

  useEffect(() => {
    if (!chartRef.current.children.length) {
      renderChart()
    }
  }, [])

  return (
    <div>
      <div className="bg-white rounded-md">
        {/* {!chart.conditions.isLoading && !chart.conditions.error ? ( */}
        <div ref={chartRef} />
        {/* {isTooltipVisible && (
          <div
            style={{
              left: tooltipPosition.x - shiftCoordinates.x - 10,
              top: tooltipPosition.y - shiftCoordinates.y - 20,
            }}
            className="absolute bg-black opacity-85 rounded-lg p-2 pointer-events-none text-sm whitespace-nowrap"
          >
            <p>
              <span className="mr-1">x:</span>
              {tooltipData.x}
            </p>
            <p>
              <span className="mr-1">y:</span>
              {tooltipData.y}
            </p>
          </div>
        )}
      </>
    ) : (
      <div className="p-3">
        {chart.conditions.error ? (
          <p className="text-red text-center">
            Error: {chart.conditions.error}
          </p>
        ) : (
          <img className="mx-auto" src="/assets/images/loader.svg" alt="" />
        )}
      </div>
    )} */}
      </div>
    </div>
  )
}

export default ConsumptionChart
